// ============================================================
// perf-sample
// ============================================================
// How long the app actually took to load, for real users.
//
// WHY THIS EXISTS
// ---------------
// DashAlign measures errors and has never measured speed. `error-monitor.ts`
// -> `log-client-error` -> `error_logs` -> the daily digest is a complete
// pipeline for crashes; there was no equivalent for latency. A grep of the
// whole repo for `PerformanceObserver`, `web-vitals`, `performance.timing` and
// `getEntriesByType` returned nothing.
//
// That is why "is it fast?" has never had an answer. Ticket 960d02e5 said
// "very slow to load"; #1627, #1634 and #1636 all shipped real improvements
// and all three ended with some version of "no authenticated session, so this
// isn't timed in a real browser". The only proposed way to settle it was to
// ask one manager to export a HAR file by hand.
//
// WHAT IT DOES
// ------------
// One sample per page load: the browser's own Navigation Timing plus a mark
// the app sets when it stops showing the loading gate. Reported through the
// SAME transport as errors (see error-monitor.sendTelemetry) and stored in the
// same table, tagged with a `perf: ` message so it groups per route and can be
// filtered back out of the error digest.
//
// PRIVACY
// -------
// The route is a PATTERN (`/tickets/:id`), never the real path, so record ids
// never reach the log — this is a stricter rule than the error path, which
// stores `source_url` verbatim. Identity follows the same consent + GPC gate
// as errors: attached only when analytics consent is granted.
//
// Pure and dependency-free so the shaping and the guards are unit-tested
// directly rather than inferred from a dashboard.

/** Prefix that marks an `error_logs` row as a performance sample, not a crash. */
export const PERF_MESSAGE_PREFIX = 'perf: ';

/** Anything slower than this is a stuck tab, not a page load. Dropped. */
export const MAX_PLAUSIBLE_LOAD_MS = 120_000;

/** The mark App.tsx sets when the loading gate gives way to real content. */
export const APP_INTERACTIVE_MARK = 'dashalign:interactive';

/**
 * Collapse a concrete path to a route pattern.
 *
 * Two jobs, and the second is the important one:
 *  1. `/tickets/<uuid>` and `/tickets/<other-uuid>` must group as one route,
 *     or every load looks like a route nobody has ever visited before.
 *  2. Record ids must not reach the log at all. A ticket id or a location id
 *     in a fingerprint is a small privacy leak that lasts as long as the row.
 */
export function routePattern(pathname: string | null | undefined): string {
  if (!pathname) return '/';
  const [path] = pathname.split(/[?#]/);
  const parts = path.split('/').filter(Boolean).map((seg) => {
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) return ':id';
    if (/^\d+$/.test(seg)) return ':n';
    // Long opaque tokens (company codes, share slugs) are ids too.
    if (seg.length >= 24 && !seg.includes('-')) return ':id';
    return seg.toLowerCase();
  });
  return parts.length ? `/${parts.join('/')}` : '/';
}

/** The `message` an `error_logs` perf row carries. Drives the fingerprint. */
export function perfMessage(pattern: string): string {
  return `${PERF_MESSAGE_PREFIX}${pattern}`;
}

/**
 * Is this `error_logs` row a perf sample rather than an error?
 *
 * The digest calls this to keep latency rows out of the crash email. The
 * server derives `fingerprint` from `message` (+ an empty stack), so the
 * prefix survives into it — see log-client-error.computeFingerprint.
 */
export function isPerfRow(messageOrFingerprint: string | null | undefined): boolean {
  return typeof messageOrFingerprint === 'string'
    && messageOrFingerprint.startsWith(PERF_MESSAGE_PREFIX);
}

/** The subset of PerformanceNavigationTiming this reads. */
export interface NavigationTimingLike {
  responseStart?: number;
  domContentLoadedEventEnd?: number;
  loadEventEnd?: number;
  duration?: number;
  type?: string;
}

export interface PerfSample {
  /** Time to first byte of the document, ms. Server + network, not the app. */
  ttfb: number | null;
  /** DOMContentLoaded, ms from navigation start. */
  domContentLoaded: number | null;
  /** The app's own readiness mark: the loading gate is gone. */
  appInteractive: number | null;
  /** window load, ms from navigation start. */
  load: number | null;
  /** How many resources the page fetched — the round-trip count #1627 is about. */
  resources: number | null;
  /** 'navigate' | 'reload' | 'back_forward' — a reload is a warm cache. */
  navigationType: string | null;
}

/** Round to whole ms, and reject values that cannot be a real measurement. */
function clean(value: number | undefined | null): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  // 0 means "never happened" in Navigation Timing, not "instant".
  if (value <= 0) return null;
  if (value > MAX_PLAUSIBLE_LOAD_MS) return null;
  return Math.round(value);
}

export function buildPerfSample(opts: {
  nav?: NavigationTimingLike | null;
  appInteractiveMs?: number | null;
  resourceCount?: number | null;
}): PerfSample {
  const nav = opts.nav ?? null;
  return {
    ttfb: clean(nav?.responseStart),
    domContentLoaded: clean(nav?.domContentLoadedEventEnd),
    appInteractive: clean(opts.appInteractiveMs),
    load: clean(nav?.loadEventEnd),
    resources:
      typeof opts.resourceCount === 'number' && opts.resourceCount >= 0
        ? Math.round(opts.resourceCount)
        : null,
    navigationType: typeof nav?.type === 'string' ? nav.type : null,
  };
}

/**
 * Is this sample worth a row?
 *
 * A sample with no timing at all measures nothing, and a load that is mostly
 * the user having left the tab in the background for an hour is worse than
 * nothing — it would drag every average it lands in. Both are dropped here
 * rather than filtered at read time, so the table only ever holds real loads.
 */
export function isReportableSample(sample: PerfSample): boolean {
  const anyTiming = sample.ttfb !== null
    || sample.domContentLoaded !== null
    || sample.appInteractive !== null
    || sample.load !== null;
  return anyTiming;
}
